/**
 * Staff RBAC — aligned with backend `FeaturePermissions` and JWT `permissions`.
 *
 * Roles: Super Admin (any `UserRoles` entry or `isSuperAdmin`) has all keys. Administrator /
 * Admin: empty `FeaturePermissions` → defaults `analytics`, `allotment`, `completed_files`, `s3`;
 * non-empty `FeaturePermissions` replaces that set. Other staff: non-empty `FeaturePermissions` only;
 * empty/missing → use `user.permissions` from POST /login or GET /validate.
 *
 * API notes: `GET /users` allows `users` or `analytics`; `GET /doctors` list aligns with `doctors` or `analytics`;
 * GraphQL fields map to the same keys — use `isInsufficientPermissionsMessage` for 403-equivalent errors.
 */

export const PERMISSION_KEYS = [
  "analytics",
  "allotment",
  "completed_files",
  "s3",
  "users",
  "doctors",
  "legals",
  "roles",
  "system",
  "registrations",
];

/** Labels for super-admin Feature access editor */
export const PERMISSION_LABELS = {
  analytics: "Analytics",
  allotment: "Allotment",
  completed_files: "Completed files",
  s3: "S3 / files",
  users: "User management",
  doctors: "Doctor management",
  legals: "Legal team management",
  roles: "Roles",
  system: "System (cache purge, ThornbillsVox)",
  registrations: "Doctor registration queue",
};

const ADMIN_DEFAULT_KEYS = ["analytics", "allotment", "completed_files", "s3"];

/** Roles that use the QA / transcriptionist SPA (/qa-dashboard), not the admin console. */
const QA_PRODUCT_ROLES = new Set([
  "QUALITY_ASSURANCE",
  "QA",
  "EDITOR",
  "TRANSCRIPTIONIST",
  "TRANSCRIPTION",
  "MT",
  "MEDICAL_TRANSCRIPTIONIST",
  "MEDICAL_TRANSCRIPTION",
  "PROJECT_MANAGER",
  "PM",
]);

/** Project Manager — full QA dashboard plus L1/L2 download oversight. */
export function isProjectManagerUser(user) {
  if (!user || typeof user !== "object") return false;
  if (user.isProjectManager === true || user.IsProjectManager === true) return true;
  const keys = collectNormalizedRoleKeys(user);
  return keys.some((k) => k === "PROJECT_MANAGER" || k === "PM");
}

function collectNormalizedRoleKeys(user) {
  if (!user || typeof user !== "object") return [];
  const keys = [];
  const add = (raw) => {
    const k = normalizeRoleKey(raw);
    if (k) keys.push(k);
  };
  add(getPrimaryRoleName(user));
  const ur = user.UserRoles ?? user.userRoles ?? [];
  for (const r of ur) {
    add(r?.RoleName ?? r?.roleName ?? r?.Role);
  }
  add(user.Role ?? user.role);
  return keys;
}

/**
 * QA / Editor / Transcriptionist / MT — product area is the QA dashboard, even if JWT includes staff `permissions`.
 */
export function isQaTrackUser(user) {
  if (!user || typeof user !== "object" || isSuperAdminUser(user)) return false;
  if (isProjectManagerUser(user)) return true;
  const keys = collectNormalizedRoleKeys(user);
  if (keys.some((k) => k === "DOCTOR")) return false;
  if (keys.some((k) => k === "ADMIN" || k === "ADMINISTRATOR")) return false;
  return keys.some((k) => QA_PRODUCT_ROLES.has(k));
}

export function normalizeRoleKey(role) {
  return (role || "").trim().toUpperCase().replace(/\s+/g, "_");
}

/** Primary role name from user document */
export function getPrimaryRoleName(user) {
  if (!user || typeof user !== "object") return "";
  const ur = user.UserRoles?.[0]?.RoleName ?? user.userRoles?.[0]?.roleName;
  if (ur) return String(ur).trim();
  return String(user.Role ?? user.role ?? "").trim();
}

export function isSuperAdminUser(user) {
  if (!user || typeof user !== "object") return false;
  if (user.isSuperAdmin === true) return true;
  const ur = user.UserRoles ?? user.userRoles ?? [];
  if (Array.isArray(ur)) {
    for (const r of ur) {
      const rk = normalizeRoleKey(r?.RoleName ?? r?.roleName ?? r?.Role ?? "");
      if (rk === "SUPER_ADMIN") return true;
    }
  }
  const flat = normalizeRoleKey(user.Role ?? user.role ?? "");
  if (flat === "SUPER_ADMIN") return true;
  return normalizeRoleKey(getPrimaryRoleName(user)) === "SUPER_ADMIN";
}

/**
 * User row (e.g. from GET /users) is an Administrator/Admin login but not Super Admin.
 * Uses `UserRoles` and top-level `Role` (not only primary index) so Admin matches API shapes.
 * Used for Access control list and effective permission resolution.
 */
export function isAdministratorAccountRow(row) {
  if (!row || typeof row !== "object") return false;
  const roles = row.UserRoles ?? row.userRoles ?? [];
  const names = roles.map((r) => normalizeRoleKey(r?.RoleName ?? r?.roleName ?? ""));
  if (names.some((n) => n === "SUPER_ADMIN")) return false;
  const flat = normalizeRoleKey(row.Role ?? row.role ?? "");
  if (flat === "SUPER_ADMIN") return false;
  return names.some((n) => n === "ADMIN" || n === "ADMINISTRATOR") || flat === "ADMIN" || flat === "ADMINISTRATOR";
}

/**
 * Effective permission keys for UI (matches server `user.permissions` when present).
 *
 * Administrator: empty/missing `FeaturePermissions` → default five keys; non-empty `FeaturePermissions`
 * replaces that set (invalid keys dropped). Same rules as POST /login and GET /validate.
 *
 * Other staff (non–super-admin, non-Administrator): non-empty `FeaturePermissions` → only those keys;
 * empty/missing → rely on `user.permissions` from API, else none client-side until validate.
 */
export function getEffectivePermissions(user) {
  if (!user || typeof user !== "object") return [];
  if (isSuperAdminUser(user)) return [...PERMISSION_KEYS];
  const perms = user.permissions;
  if (Array.isArray(perms) && perms.length > 0) return perms;

  const fpRaw = user.FeaturePermissions ?? user.featurePermissions;
  const fp = Array.isArray(fpRaw) ? fpRaw.filter((k) => PERMISSION_KEYS.includes(k)) : [];

  if (isAdministratorAccountRow(user)) {
    if (fp.length > 0) return fp;
    return [...ADMIN_DEFAULT_KEYS];
  }

  if (fp.length > 0) return fp;
  return [];
}

export function hasPermission(user, key) {
  if (!key) return true;
  if (isSuperAdminUser(user)) return true;
  const keys = Array.isArray(key) ? key : [key];
  const eff = getEffectivePermissions(user);
  return keys.some((k) => eff.includes(k));
}

/**
 * Map dashboard tab key -> permission key(s). Use an array when the tab may be shown if the user has
 * any of the keys (matches backend: e.g. doctors list for `analytics`, registration queue for `registrations`).
 */
export const TAB_PERMISSION = {
  analytics: "analytics",
  allotment: "allotment",
  completedFiles: "completed_files",
  doctors: ["doctors",
  "legals", "analytics", "registrations"],
  users: "users",
  roles: "roles",
};

export function isTabAllowed(user, tabKey) {
  const p = TAB_PERMISSION[tabKey];
  if (!p) return false;
  return hasPermission(user, p);
}

/** POST /graphql (or similar): treat as HTTP 403 for UI when the server returns this message. */
export function isInsufficientPermissionsMessage(message) {
  return typeof message === "string" && /insufficient permissions/i.test(message);
}

export function getAllowedTabKeys(user) {
  return Object.keys(TAB_PERMISSION).filter((k) => isTabAllowed(user, k));
}

/**
 * First staff dashboard path for an authenticated user (after login / guard redirect).
 */
export function getDefaultStaffPath(user) {
  if (!user) return "/login";
  if (isSuperAdminUser(user)) return "/super-admin-dashboard";
  if (isQaTrackUser(user)) return "/qa-dashboard";
  const tabs = getAllowedTabKeys(user);
  if (tabs.length === 0) return "/login";
  const first = tabs[0];
  if (first === "completedFiles") return "/completed-files";
  return "/dashboard";
}

export function getSafeRedirectPathForUser(user) {
  return getDefaultStaffPath(user);
}

/** Dashboard shell or S3-only staff area */
export function canAccessStaffConsole(user) {
  if (!user || typeof user !== "object") return false;
  if (isQaTrackUser(user)) return false;
  if (isSuperAdminUser(user)) return true;
  if (getAllowedTabKeys(user).length > 0) return true;
  if (hasPermission(user, "s3")) return true;
  return false;
}
